import { useEffect, useState } from 'react';
import Pusher from 'pusher-js';
import axiosInstance from '../services/server/axios';
import { withLastMessages, setCachedLastMessage } from '../services/chat/lastMessages';

/**
 * Shared support-chat inbox state.
 *
 * Every component that calls `useUnreadChat()` reads from a single module-level
 * store backed by one poller + one realtime socket, so mounting the hook in the
 * navbar, the sidebar and a page at the same time still results in just one
 * `/conversations` request per cycle and one Pusher connection.
 *
 * Returns `{ count, conversations, refresh }`:
 *  - `count`         total unread messages across the user's conversations
 *  - `conversations` the conversation list, newest activity first
 *  - `refresh()`     force an immediate reload
 */

const POLL_MS = 25000;

const store = {
  count: 0,
  conversations: [],
  listeners: new Set(),
  timer: null,
  inFlight: false,
  pusher: null,
  channelName: null,
};

const activityAt = (c) =>
  new Date(c.last_message?.created_at || c.updated_at || 0).getTime() || 0;

const sortList = (list) =>
  (list || []).slice().sort((a, b) => activityAt(b) - activityAt(a) || b.id - a.id);

const recompute = () => {
  store.count = store.conversations.reduce((sum, c) => sum + (Number(c.unread) || 0), 0);
};

const emit = () => {
  const snapshot = { count: store.count, conversations: store.conversations };
  store.listeners.forEach((fn) => fn(snapshot));
};

const currentUserId = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}')?.id ?? null;
  } catch {
    return null;
  }
};

const load = async () => {
  if (store.inFlight || !localStorage.getItem('authToken')) return;
  store.inFlight = true;
  try {
    const { data } = await axiosInstance.get('/conversations');
    const known = new Map(store.conversations.map((c) => [c.id, c]));
    const base = (data.conversations || []).map((c) =>
      c.last_message || !known.get(c.id)?.last_message
        ? c
        : { ...c, last_message: known.get(c.id).last_message, updated_at: known.get(c.id).updated_at || c.updated_at },
    );
    store.conversations = sortList(base);
    recompute();
    emit();

    // Fill missing previews from the threads (shared session cache).
    const beforeById = new Map(store.conversations.map((c) => [c.id, c.last_message]));
    const enriched = sortList(await withLastMessages(store.conversations));
    if (enriched.some((c) => beforeById.get(c.id) !== c.last_message)) {
      store.conversations = enriched;
      recompute();
      emit();
    }
  } catch {
    /* silent — the badge is non critical */
  } finally {
    store.inFlight = false;
  }
};

// Patch the list straight from a realtime event so previews and ordering are
// correct even when the API response carries no `last_message` field.
const applyIncoming = (incoming) => {
  if (!incoming?.conversation_id) return;
  const meId = currentUserId();
  const mine = incoming.sender?.id === meId || incoming.sender_id === meId;
  const at = incoming.created_at || new Date().toISOString();
  setCachedLastMessage(incoming.conversation_id, {
    body: incoming.body,
    sender_id: incoming.sender?.id ?? incoming.sender_id,
    created_at: at,
  });

  const known = store.conversations.some((c) => c.id === incoming.conversation_id);

  if (!known) {
    load();
    return;
  }

  store.conversations = sortList(
    store.conversations.map((c) =>
      c.id === incoming.conversation_id
        ? {
            ...c,
            last_message: {
              body: incoming.body,
              sender_id: incoming.sender?.id ?? incoming.sender_id,
              created_at: at,
            },
            updated_at: at,
            unread: mine ? c.unread || 0 : (c.unread || 0) + 1,
          }
        : c,
    ),
  );
  recompute();
  emit();
};

const onExternal = (event) => {
  if (typeof event.detail === 'number') {
    store.count = event.detail;
    emit();
  }
  load();
};

const onVisible = () => {
  if (document.visibilityState === 'visible') load();
};

const connectRealtime = () => {
  const id = currentUserId();
  if (store.pusher || !id || !localStorage.getItem('authToken')) return;
  try {
    store.pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY || '38f74624900631a94686', {
      cluster: process.env.REACT_APP_PUSHER_CLUSTER || 'mt1',
      forceTLS: true,
      channelAuthorization: {
        endpoint: `${process.env.REACT_APP_BASE_URL}/pusher/auth`,
        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
      },
    });
    store.channelName = `private-user-${id}`;
    const channel = store.pusher.subscribe(store.channelName);
    channel.bind('new-message', applyIncoming);
    channel.bind('message-deleted', load);
    channel.bind('conversation-closed', load);
  } catch {
    store.pusher = null;
  }
};

const startPolling = () => {
  if (store.timer) return;
  window.addEventListener('mf-chat:unread', onExternal);
  document.addEventListener('visibilitychange', onVisible);
  store.timer = setInterval(load, POLL_MS);
  connectRealtime();
  load();
};

const stopPolling = () => {
  if (store.listeners.size > 0 || !store.timer) return;
  clearInterval(store.timer);
  store.timer = null;
  window.removeEventListener('mf-chat:unread', onExternal);
  document.removeEventListener('visibilitychange', onVisible);
  if (store.pusher) {
    try {
      if (store.channelName) store.pusher.unsubscribe(store.channelName);
      store.pusher.disconnect();
    } catch {
      /* ignore */
    }
    store.pusher = null;
    store.channelName = null;
  }
};

export default function useUnreadChat() {
  const [state, setState] = useState({
    count: store.count,
    conversations: store.conversations,
  });

  useEffect(() => {
    const listener = (snapshot) => setState(snapshot);
    store.listeners.add(listener);
    startPolling();
    return () => {
      store.listeners.delete(listener);
      stopPolling();
    };
  }, []);

  return { count: state.count, conversations: state.conversations, refresh: load };
}
